import { cva, type VariantProps } from 'class-variance-authority'

/** Shared class recipes of the kit (class-variance-authority). */
export const buttonVariants = cva(
  'inline-flex select-none items-center justify-center gap-2 whitespace-nowrap rounded-input ' +
  'border border-transparent font-medium leading-[1.35] no-underline ' +
  // Pressed, a button sinks a pixel: the click is felt before the answer comes.
  'transition-[color,background-color,border-color,box-shadow,transform] duration-150 enabled:active:translate-y-px ' +
  'cursor-pointer disabled:cursor-not-allowed disabled:opacity-55 disabled:shadow-none ' +
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
  {
    variants: {
      variant: {
        primary: 'bg-primary text-primary-foreground shadow-xs hover:bg-primary-hover hover:shadow-sm',
        secondary: 'bg-muted text-foreground hover:bg-subtle',
        // A row's or a card's actions: framed, so they read as buttons and not as text.
        outline: 'border-input bg-card text-foreground shadow-xs hover:border-border-strong hover:bg-muted',
        ghost: 'bg-transparent text-foreground hover:bg-muted',
        danger: 'bg-danger text-white shadow-xs hover:brightness-95',
        // A destructive action among others (switch off, revoke): framed, in the danger colour, never filled.
        'danger-outline': 'border-danger/35 bg-card text-danger shadow-xs hover:border-danger/60 hover:bg-danger-soft',
        success: 'bg-success text-white shadow-xs hover:brightness-95',
        icon: 'relative h-9 w-9 flex-none bg-transparent p-0 text-muted-foreground hover:bg-muted hover:text-foreground',
      },
      size: {
        sm: 'min-h-8 gap-1.5 px-2.5 py-1 text-[13px]',
        md: 'min-h-9 px-3.5 py-1.5 text-[13px]',
        lg: 'min-h-10 px-[18px] py-[9px] text-[14px]',
      },
      block: { true: 'w-full' },
    },
    compoundVariants: [{ variant: 'icon', size: 'md', class: 'min-h-9 px-0 py-0' }],
    defaultVariants: { variant: 'primary', size: 'md' },
  },
)
export type ButtonVariant = NonNullable<VariantProps<typeof buttonVariants>['variant']>

export const TAG_TONES = {
  neutral: 'border-border bg-muted text-foreground',
  success: 'border-success/15 bg-success-soft text-success-soft-fg',
  warning: 'border-warning/15 bg-warning-soft text-warning-soft-fg',
  danger: 'border-danger/15 bg-danger-soft text-danger-soft-fg',
  primary: 'border-primary/20 bg-primary/10 text-primary',
  info: 'border-info/15 bg-info-soft text-info-soft-fg',
} as const
export type Tone = keyof typeof TAG_TONES

export const BANNER_TONES = {
  info: 'bg-info-soft text-info-soft-fg',
  success: 'bg-success-soft text-success-soft-fg',
  warning: 'bg-warning-soft text-warning-soft-fg',
  danger: 'bg-danger-soft text-danger-soft-fg',
} as const
export type BannerTone = keyof typeof BANNER_TONES

/** Common look of text inputs and selects. */
export const FIELD_CLASSES =
  'min-w-0 rounded-input border border-input bg-card px-3 text-[13px] text-foreground ' +
  'transition-[border-color,box-shadow] [transition-duration:120ms] placeholder:text-muted-foreground ' +
  'hover:enabled:border-border-strong focus:border-ring focus:outline-none focus:ring-2 focus:ring-ring/20 ' +
  'disabled:cursor-not-allowed disabled:opacity-55'
